/**
 * Image intake for the composer: validate, downscale, and shape for the wire.
 *
 * The canvas work is injected as `encode` because happy-dom has no canvas at all
 * (`getContext("2d")` is null, `createImageBitmap` undefined), so the real path
 * cannot run under test. The arithmetic lives in `fitWithin`, which is pure.
 */

import { DAEMON_URL } from "../config";
import { HttpError } from "../api/errors";
import type { AttachmentMeta } from "../api/types";

/**
 * Anthropic's optimal max edge: larger costs tokens without helping accuracy.
 *
 * Both halves of the guard earn their place. `isFinite` rejects a non-numeric
 * override, which would otherwise reach `fitWithin` as NaN and set a canvas
 * dimension to NaN — no error, just silently broken images. The `>= 1` floor
 * rejects `""`, which parses to a perfectly finite 0 and would scale every
 * image down to a single pixel.
 */
const configuredEdge = Number(import.meta.env.VITE_ATTACHMENT_MAX_EDGE);
export const MAX_EDGE = Number.isFinite(configuredEdge) && configuredEdge >= 1 ? configuredEdge : 1568;

/**
 * The largest encoded payload worth sending. Without it the failure is invisible
 * until send, because `canvasEncode` never downscales a GIF or a WebP and passes
 * a PNG through untouched when it already fits MAX_EDGE.
 *
 * This is one half of a pair. The daemon enforces its own `attachmentMaxBytes`
 * (`src/config.ts`) and 413s anything larger; both default to 5 MiB, and both are
 * env-tunable — `VITE_ATTACHMENT_MAX_BYTES` here, `ATTACHMENT_MAX_BYTES` there.
 * A deployment that changes one must change the other, or the gap reopens: set
 * the daemon lower and the client waves through images it will reject, set it
 * higher and the client refuses images it would have taken.
 *
 * Two asymmetries to know about. This value is baked in at build time, so
 * changing it means rebuilding the frontend, not just restarting it. And a
 * malformed value stops the daemon at startup, where this falls back to the
 * default rather than shipping a bundle that cannot size anything.
 */
const configuredBytes = Number(import.meta.env.VITE_ATTACHMENT_MAX_BYTES);
export const MAX_BYTES = Number.isFinite(configuredBytes) && configuredBytes >= 1 ? configuredBytes : 5_242_880;

/**
 * How many images one message may carry. The other half of the same pair as
 * MAX_BYTES: the daemon's `attachmentMaxPerMessage` (`src/config.ts`) 413s
 * anything over its own limit, both default to 5, and both are env-tunable —
 * `VITE_ATTACHMENT_MAX_PER_MESSAGE` here, `ATTACHMENT_MAX_PER_MESSAGE` there.
 * Move one without the other and the composer either accepts images the daemon
 * will reject or refuses ones it would have taken. Same build-time and
 * malformed-value caveats as MAX_BYTES.
 */
const configuredFiles = Number(import.meta.env.VITE_ATTACHMENT_MAX_PER_MESSAGE);
export const MAX_FILES = Number.isFinite(configuredFiles) && configuredFiles >= 1 ? configuredFiles : 5;

/** The only four types the Anthropic API accepts. */
export const SUPPORTED = ["image/png", "image/jpeg", "image/gif", "image/webp"] as const;

export type Attachment = {
  /** Local only — a React key. The server generates the id it stores. */
  id: string;
  name: string;
  mediaType: string;
  dataUrl: string;
  base64: string;
  width: number;
  height: number;
  bytes: number;
  /**
   * Set when the image is stored on the daemon as part of a queued prompt. An
   * edit sends this id in `keep` instead of the bytes. A local image the member
   * attached keeps its own `id`, `dataUrl` and `base64` and only gains this
   * field (`mergeStoredIds`). An image loaded from the server with no local
   * copy (`storedAttachment`) has `dataUrl` pointing at the attachment route
   * and an EMPTY `base64` until `rehydrateStoredAttachments` fills it.
   */
  storedId?: string;
};

export type OutgoingImage = {
  name: string;
  mediaType: string;
  data: string;
  width: number;
  height: number;
};

export type EncodeFn = (file: File, maxEdge: number) => Promise<{ dataUrl: string; width: number; height: number }>;

export function isSupportedImage(file: { type: string }): boolean {
  return (SUPPORTED as readonly string[]).includes(file.type);
}

/**
 * Scale (w,h) to fit inside a maxEdge box, preserving ratio. Always whole
 * pixels, never below 1 — on both paths, including the one that already fits,
 * so a caller can rely on that for any input rather than only for a downscale.
 */
export function fitWithin(w: number, h: number, maxEdge: number): { width: number; height: number } {
  const clamp = (n: number): number => Math.max(1, Math.round(n));
  if (w <= maxEdge && h <= maxEdge) return { width: clamp(w), height: clamp(h) };
  const scale = maxEdge / Math.max(w, h);
  return { width: clamp(w * scale), height: clamp(h * scale) };
}

/**
 * The real encoder. GIF and WebP pass through untouched: re-encoding a GIF via
 * canvas would drop its animation, and the byte cap is protection enough for
 * both. PNG stays PNG because screenshots are flat colour that JPEG smears.
 */
const canvasEncode: EncodeFn = async (file, maxEdge) => {
  const bitmap = await createImageBitmap(file);
  // An ImageBitmap holds its decoded pixels off the JS heap, where GC tracks
  // them poorly. A 20 MP phone photo is ~80 MB decoded and five are allowed per
  // message, so release each one rather than waiting to be collected.
  try {
    const passthrough = file.type === "image/gif" || file.type === "image/webp";
    const { width, height } = passthrough
      ? { width: bitmap.width, height: bitmap.height }
      : fitWithin(bitmap.width, bitmap.height, maxEdge);

    if (passthrough || (width === bitmap.width && height === bitmap.height && file.type === "image/png")) {
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result));
        reader.onerror = () => reject(new Error("could not read file"));
        reader.readAsDataURL(file);
      });
      return { dataUrl, width, height };
    }

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("canvas unavailable");
    ctx.drawImage(bitmap, 0, 0, width, height);
    const mediaType = file.type === "image/png" ? "image/png" : "image/jpeg";
    return { dataUrl: canvas.toDataURL(mediaType, 0.9), width, height };
  } finally {
    bitmap.close();
  }
};

/**
 * A local id, only ever a React key — the server generates the id it stores.
 * Same shape as `useChatStream.ts`'s `nextId`, and for the same reason:
 * `crypto.randomUUID` is secure-context only, so it is simply undefined when the
 * dev server is reached over a LAN IP to test on a phone.
 */
function localId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

export async function fileToAttachment(file: File, encode: EncodeFn = canvasEncode): Promise<Attachment> {
  if (!isSupportedImage(file)) throw new Error(`unsupported image type: ${file.type || "unknown"}`);
  const { dataUrl, width, height } = await encode(file, MAX_EDGE);
  const comma = dataUrl.indexOf(",");
  const base64 = dataUrl.slice(comma + 1);
  const mediaType = dataUrl.slice(5, dataUrl.indexOf(";"));
  // After encoding, not before: a downscale routinely brings an oversized
  // source under the cap, and rejecting on the source size would refuse images
  // that would have been perfectly fine to send.
  const bytes = Math.floor((base64.replace(/=+$/, "").length * 3) / 4);
  if (bytes > MAX_BYTES) throw new Error(`image too large: ${bytes} bytes exceeds the ${MAX_BYTES} byte limit`);
  // Symmetric with the cap, and with the daemon's own 400 on an empty payload:
  // an encoder that yields nothing would otherwise produce an Attachment with an
  // empty base64 and a media type parsed out of a malformed data URL.
  if (bytes === 0) throw new Error("image encoded to an empty payload");
  return {
    id: localId(),
    name: file.name || "image",
    mediaType,
    dataUrl,
    base64,
    width,
    height,
    bytes,
  };
}

export function toOutgoing(a: Attachment): OutgoingImage {
  return { name: a.name, mediaType: a.mediaType, data: a.base64, width: a.width, height: a.height };
}

/**
 * A stored image as an Attachment, so the Composer's thumbnails and edit flow
 * handle it like a local one. Same URL Message.tsx builds for a stored image.
 * Use it only when the client holds no local copy of the image (a reload, or
 * another tab's prompt); otherwise `mergeStoredIds` keeps the local one.
 */
export function storedAttachment(conversationId: number, meta: AttachmentMeta): Attachment {
  return {
    id: meta.id,
    storedId: meta.id,
    name: meta.name,
    mediaType: meta.mediaType,
    dataUrl: `${DAEMON_URL}/attachments/${conversationId}/${encodeURIComponent(meta.id)}`,
    base64: "",
    width: meta.width,
    height: meta.height,
    bytes: meta.bytes,
  };
}

/**
 * Put the daemon's stored ids on the member's own Attachment objects instead
 * of replacing them. Replacing them would change every `id`, and the Composer
 * compares attachment lists by id, so an open edit would read as modified and
 * be PATCHed again with every image as bytes.
 *
 * A local attachment that already has a `storedId` is matched to the stored
 * image with that id (it was kept by an edit). The rest are matched, in order,
 * to the stored images no kept one claimed: the daemon stores kept images
 * first and new ones after, in the order they were sent. Answers `null` when
 * the lists do not describe the same images, so the caller falls back to
 * `storedAttachment` for the server's list.
 */
export function mergeStoredIds(local: Attachment[], stored: AttachmentMeta[]): Attachment[] | null {
  if (local.length !== stored.length) return null;
  const byId = new Set(stored.map((m) => m.id));
  const claimed = new Set<string>();
  for (const a of local) {
    if (a.storedId === undefined) continue;
    if (!byId.has(a.storedId)) return null;
    claimed.add(a.storedId);
  }
  const fresh = stored.filter((m) => !claimed.has(m.id));
  let next = 0;
  return local.map((a) => {
    if (a.storedId !== undefined) return a;
    const meta = fresh[next++]!;
    return { ...a, storedId: meta.id };
  });
}

/** Base64 without a data-URL prefix, chunked so a large image does not overflow the argument limit. */
function bytesToBase64(bytes: Uint8Array): string {
  let binary = "";
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return btoa(binary);
}

/**
 * Fill in the bytes of every stored image that has none, before a draft that
 * holds one is sent or queued. That happens when a prompt loaded from the
 * server (no local copy) is edited and then drains while the edit is open: the
 * Composer keeps the edit as a draft, and `toOutgoing` would send its stored
 * images with empty data. Attachments that already have bytes pass through by
 * identity and nothing is fetched. `id` and `storedId` never change. Throws an
 * HttpError when the daemon refuses and an Error when it returns nothing: an
 * empty image must never reach the wire.
 */
export async function rehydrateStoredAttachments(attachments: Attachment[]): Promise<Attachment[]> {
  return Promise.all(
    attachments.map(async (a) => {
      if (a.storedId === undefined || a.base64 !== "") return a;
      const res = await fetch(a.dataUrl, { credentials: "include" });
      if (!res.ok) throw new HttpError(res.status, `could not load the stored image ${a.name}`);
      const bytes = new Uint8Array(await res.arrayBuffer());
      if (bytes.length === 0) throw new Error(`the stored image ${a.name} is empty`);
      return { ...a, base64: bytesToBase64(bytes) };
    }),
  );
}
